/* ---------------------------------------------------------------------------
   The screen header: a kicker, a large title, and whatever that screen offers.

   One rule for the right-hand side. Screens that DO something there get
   controls; screens that only report get a line of provenance instead — who
   issued this view, and as at when. A dashboard without a date on it is a
   dashboard nobody can quote.
   --------------------------------------------------------------------------- */

import { h, ICONS, type Child } from '../core/dom.ts';
import { actions, can, store, type State } from '../core/store.ts';
import { button, seg } from './widgets.ts';
import { findBridge, mediumDate } from '../data/derive.ts';

import { visibleRegister } from '../screens/register.ts';
import { t } from '../data/locale.ts';
import { agency } from '../tenant/agency.ts';
import { active } from '../tenant/registry.ts';
import { join, phrase } from '../data/recorded.ts';
import { chainage } from '../tenant/format.ts';
import { PERIOD_LABEL, periodTitle } from '../data/periods.ts';

interface Head {
  crumb: string;
  title: string;
  /** A line under the title — used where the title alone does not locate the thing. */
  sub?: string;
  /** Right-hand provenance, one line per entry. */
  meta?: string[];
  /** Right-hand controls. Mutually exclusive with `meta` in practice. */
  tools?: Child[];
  /** A view switch, drawn ahead of the provenance line. */
  view?: Child;
}

/** The home page's Both / Register / Network switch. */
function homeViewSwitch(s: Readonly<State>): HTMLElement {
  const el = seg([
    {
      label: 'Both',
      leadIcon: ICONS.stacked,
      on: s.homeView === 'both',
      title: 'Show the register and the network',
      onClick: () => actions.setHomeView('both'),
    },
    {
      label: 'Register',
      leadIcon: ICONS.list,
      on: s.homeView === 'register',
      title: 'Show the register only',
      onClick: () => actions.setHomeView('register'),
    },
    {
      label: 'Network',
      leadIcon: ICONS.map,
      on: s.homeView === 'network',
      title: 'Show the network only',
      onClick: () => actions.setHomeView('network'),
    },
  ]);
  el.classList.add('seg--head');
  el.setAttribute('aria-label', 'View');
  return el;
}

/** Download the current register as CSV. */
function exportRegisterCsv(): void {
  const s = store.get();
  const rows = visibleRegister();
  const head = [
    'Bridge ID',
    'Inventory code',
    'Name',
    'Route',
    'Road',
    'km',
    'District',
    'Province',
    'River',
    'Superstructure',
    'Spans',
    'Length (m)',
    'Deck width (m)',
    'Carriageway (m)',
    'Built',
    'Division office',
    ...active().extras.map((f) => f.label),
  ];
  const esc = (v: unknown) => {
    const str = String(v ?? '');
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
  };
  /* AN EMPTY CELL IS THE RIGHT ANSWER for a value nobody has recorded, which is
     why nothing here goes through `metres()` or the em dash the screens use. A
     spreadsheet reads a dash as text and refuses to sum the column it is in; it
     reads a blank as a blank and sums around it. Nothing is padded, defaulted or
     zeroed. */
  const dp = (v: number | null, digits: number) => (v == null ? '' : v.toFixed(digits));
  const body = rows.map((b) =>
    [
      b.id, b.inventoryCode ?? '', b.name, b.route ?? '', b.roadName, dp(b.km, 1),
      b.district ?? '', b.province ?? '', b.river ?? '', b.type ?? '', b.spans ?? '',
      dp(b.length, 2), dp(b.deckWidth, 1), dp(b.carriageway, 1), b.year ?? '',
      b.divisionOffice ?? '',
      /* The tenant's own columns, in the order its profile declares them. A
         department whose register is largely extra fields — DPWH, whose package
         and station are the two things everybody sorts by — used to get a CSV
         with none of them in it. */
      ...active().extras.map((f) => b.tenantFields[f.key] ?? ''),
    ]
      .map(esc)
      .join(','),
  );

  const csv = [head.join(','), ...body].join('\r\n');
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `bridge-register-${s.district === 'All districts' ? 'all' : s.district.toLowerCase().replace(/\s+/g, '-')}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function head(s: Readonly<State>): Head {
  const b = findBridge(s.selectedId);

  switch (s.screen) {
    case 'dashboard':
      /* The home page IS the register now — the list of structures and the
         network they sit on — and everything about one structure is in the
         applications. So it is headed with what it is, and carries the
         register's own tools: the export, and a new record. */
      return {
        crumb: agency().unitShort,
        title: 'Bridge register',
        view: homeViewSwitch(s),
        tools: [
          button('Export CSV', { iconPath: ICONS.import, onClick: exportRegisterCsv }),
          can(s, 'inventory.edit') || can(s, 'structure.create')
            ? button('New bridge', {
                primary: true,
                iconPath: ICONS.plus,
                title: 'Add a structure to the register',
                onClick: () => actions.openNewBridge(),
              })
            : null,
        ],
      };

    case 'list':
      return {
        crumb: 'Inventory register',
        title: 'Bridge register',
        tools: [button('Export CSV', { iconPath: ICONS.plus, onClick: exportRegisterCsv })],
      };

    case 'network':
      return {
        crumb: 'Network',
        title: 'Route schematic',
        meta: [
          'A tile map of the located structures, with route strips by chainage beneath it.',
          'Positions are map pins, not surveyed points \u2014 chainage is the location of record.',
        ],
      };

    case 'detail':
      /* Nothing to head. `b` is null only where the register is empty, and a
         crumb and title assembled from no record would be a line of separators.
         The screen below says the register is empty; this says the same. */
      if (b === null) {
        return {
          crumb: 'Bridge record',
          title: 'No structure to show',
          sub: 'This register has no structures in it yet.',
        };
      }
      return {
        /* Assembled rather than interpolated. Every part after the id can be
           unrecorded, and a template puts the word "null" on screen for each
           one that is — which TypeScript does not catch, because interpolating
           a null is legal. `join` and `phrase` drop what is missing. */
        crumb: join([b.id, phrase([b.district, b.province], ', ')], ' · '),
        title: b.name,
        sub: join(
          [
            phrase([b.route, b.roadName], ' '),
            chainage(b.km),
            b.river == null ? null : `crossing ${b.river}`,
          ],
          ' · ',
        ),
      };

    case 'report':
      return {
        crumb: 'Print view',
        title: 'Bridge inventory sheet',
        tools: [button(t('Print'), { primary: true, iconPath: ICONS.print, onClick: () => window.print() })],
      };

    case 'sheet':
      return {
        crumb: 'Print view',
        title: 'Bridge Inspection Summary',
        tools: [button(t('Print'), { primary: true, iconPath: ICONS.print, onClick: () => window.print() })],
      };

    case 'works':
      /* The period, in the crumb, because it is what distinguishes this
         document from the same document last month — and the print button,
         because this is one of the three screens somebody prints. The
         cadence and the dates are named again on the screen itself: a printed
         sheet that says only "Monthly" is a sheet nobody can file. */
      return {
        crumb: `${PERIOD_LABEL[s.worksPeriod]} report`,
        title: 'Construction progress',
        sub: periodTitle(s.worksPeriod, s.worksRange),
        /* The contract of the structure in hand, where its baseline carries
           one (LC-42): the sheet names what it reports against. */
        meta: [
          agency().unitShort,
          ...(s.baseline?.contract?.contractNo ? [`Contract ${s.baseline.contract.contractNo}`] : []),
          `compiled ${mediumDate(new Date())}`,
        ],
        tools: [button(t('Print'), { primary: true, iconPath: ICONS.print, onClick: () => window.print() })],
      };

    case 'summary':
      return {
        crumb: 'Print view',
        title: t('Bridge Network Summary'),
        tools: [button(t('Print'), { primary: true, iconPath: ICONS.print, onClick: () => window.print() })],
      };

    case 'board':
      /* One structure, headed as its record is (LC-30): the dashboard is about
         it and nothing else. */
      if (b === null) {
        return { crumb: 'Bridge dashboard', title: 'No structure to show' };
      }
      return {
        crumb: join([b.id, phrase([b.district, b.province], ', ')], ' · '),
        title: b.name,
        sub: 'Bridge dashboard',
        meta: [agency().unitShort, `as at ${mediumDate(new Date())}`],
      };

    case 'schedule':
      /* The structure, as the record's head names it — this is a page about one
         bridge and the crumb has to say which, exactly as the record's does. The
         app is named in the title bar and the nav pane, so it is not repeated
         here. */
      if (b === null) {
        return { crumb: 'Programme', title: 'No structure to show' };
      }
      return {
        crumb: join([b.id, phrase([b.district, b.province], ', ')], ' · '),
        title: b.name,
        sub: 'Programme, matching and variance',
        meta: [agency().unitShort, `as at ${mediumDate(new Date())}`],
      };

    case 'site':
      if (b === null) {
        return { crumb: 'Site record', title: 'No structure to show' };
      }
      return {
        crumb: join([b.id, phrase([b.district, b.province], ', ')], ' · '),
        title: b.name,
        sub: 'Site record — work, returns, incidents and documents',
        meta: [agency().unitShort, `as at ${mediumDate(new Date())}`],
      };

    case 'options':
      /* No tools and no provenance line. Every control on this screen sits in the
         card it belongs to, because they are unrelated to each other — signing
         out, sending the queue and switching language have nothing in common
         except being this device's business — and a header full of them would
         imply an order they do not have. */
      return {
        crumb: agency().unitShort,
        title: t('Options'),
      };

    case 'people':
      /* Provenance rather than tools. Every control on this screen belongs to a
         row or to the form above them, and a header button would have to be
         "Grant a role" — which is already the first thing in the list's own
         head, next to the list it changes. */
      return {
        crumb: 'Administration',
        title: 'People & roles',
        sub: 'Who may use this register, and what they may do',
        meta: [agency().unitShort, `as at ${mediumDate(new Date())}`],
      };

    case 'guide':
      /* Provenance rather than tools, which is the rule at the top of this file:
         this screen reports and does nothing. What "as at" means here is not the
         register's date but this build's — a guide describes the application it
         shipped with, and a reader who finds a control missing needs to be able
         to say which version they were reading. */
      return {
        crumb: 'Help',
        title: t('Guide'),
        sub: 'Using the web app and the field app',
        meta: [
          agency().unitShort,
          `Describes this build · read ${mediumDate(new Date())}`,
        ],
      };
  }
}

/**
 * The install offer, in the field shell's own header.
 *
 * THE ONE EXCEPTION TO THE RULE IN pagehead() BELOW, and it is put here rather
 * than in that rule's `tools` list so that the exception cannot quietly become a
 * door the desk sets walk back through.
 *
 * The rule exists because the desk header's tools are desk work — a CSV export, a
 * print document — handed to somebody standing in a river. This is not one of
 * those, and the difference is not that it is more important. It is that it is
 * not an action on the screen it sits above. It acts on the device, it can be
 * offered at most once per visit, and once taken it never appears again.
 *
 * IT IS HERE BECAUSE THE CARD ON OPTIONS WAS NOT ENOUGH. The whole point of
 * installing is that it happens on somebody's first visit, in an office with
 * signal, before the phone ever goes out — and a first visit does not open the
 * third tab and scroll it. So the offer goes on the screen the field shell lands
 * on, next to the title, where somebody is already looking. The Options card
 * stays: it is the one that explains what installing does, and the only thing
 * left to say on a browser that never makes an offer to capture.
 *
 * The register screen only. On the record, the header sits directly above pinned
 * Log inspection and Log defect buttons, and a third filled pill above the two
 * that matter at a structure would be competing with them for the same thumb.
 */
function installOffer(s: Readonly<State>): Child {
  if (s.shell !== 'field' || s.screen !== 'list') return null;
  /* Nothing captured, or nothing left to do — see src/core/install.ts. A button
     drawn on any other basis would do nothing when pressed. */
  if (!s.canInstall || s.installed) return null;

  return h(
    'div',
    { class: 'pagehead__tools' },
    button('Install', {
      primary: true,
      iconPath: ICONS.install,
      title: 'Add Bridge Watch to this device’s home screen',
      onClick: () => void actions.promptInstall(),
    }),
  );
}

export function pagehead(s: Readonly<State>): HTMLDivElement {
  const { crumb, title, sub, meta, view, tools: deskTools } = head(s);

  /* NO HEADER TOOLS ON THE FIELD SHELL, for any screen.
     Every action the field shell offers lives in the screen that owns it — the
     record's pinned Log inspection / Log defect bar, the map's way out, the
     Options cards — and the desk sets are the wrong ones anyway: the register's
     is a CSV export, and the record's includes a print document. This was first
     written as a guard on the record alone, and the register's "Export CSV" went
     on rendering on a phone until a check asked. One rule, because the reason is
     the same for all of them.

     ONE EXCEPTION, and it is not in this list: the install offer, which is not an
     action on any screen but an offer about the device, made at most once. It is
     assembled by installOffer above and appended separately, so that widening it
     cannot be done by adding to `deskTools` and hoping. */
  const tools = s.shell === 'field' ? undefined : deskTools;

  return h(
    'div',
    { class: 'pagehead' },
    h(
      'div',
      { style: 'min-width:0' },
      h('div', { class: 'pagehead__crumb', text: crumb }),
      h('h1', { class: 'pagehead__title', text: title }),
      sub ? h('div', { class: 'pagehead__sub', text: sub }) : null,
    ),
    h('span', { class: 'spacer' }),
    s.shell === 'field' ? null : view,
    meta
      ? h('div', { class: 'pagehead__meta' }, ...meta.map((line) => h('div', { text: line })))
      : null,
    tools ? h('div', { class: 'pagehead__tools' }, ...tools) : null,
    installOffer(s),
  );
}
